/* Live read of the `interactions` collection for one CRM drawer.

   No composite indexes exist for this collection yet, so every query here uses
   a single where() clause and the sort happens on the client. Adding an
   orderBy() would need (clientId, date desc) and (contactIds array, date desc)
   deployed first (docs/client-interactions-spec.md).

   The admin-only Firestore rule is also not deployed yet. Until it is, a read
   fails with permission-denied, which lands in `error` and renders as a single
   muted line in the timeline instead of an exception. */
import { useEffect, useState } from "react";
import { db, collection, onSnapshot, query } from "../../firebase.js";
import { where } from "firebase/firestore";
import { sortRecords } from "./activityModel.js";

const COLLECTION = "interactions";

/* How long a permission-denied answer is trusted before the next drawer open
   tries again. A permanent per-page-load latch was wrong in both directions:
   it kept re-denying after the rule shipped or after auth claims arrived, and
   the only way back was a reload. One minute is long enough that a drawer
   opened repeatedly does not spam a failing listener, short enough that a
   deploy or a re-auth is picked up without one. */
const DENIED_RETRY_MS = 60_000;
let deniedAt = 0;

/** The contact ids, as a value that survives being an effect dependency.
 *  JSON, not join(","): an id containing a comma would be split back into two
 *  ids, and an empty array and [""] would look identical.
 *  Deduped and sorted, because the SET of ids is what the queries depend on —
 *  a caller that reorders or repeats them would otherwise tear down every
 *  listener and rebuild it to ask the same questions. */
export function serializeIds(ids) {
  return JSON.stringify([...new Set((ids || []).filter(Boolean).map(String))].sort());
}

/** The inverse. A malformed key reads as "no ids" rather than throwing inside
 *  an effect, where a throw would take the whole drawer down. */
export function deserializeIds(key) {
  try {
    const v = JSON.parse(key || "[]");
    return Array.isArray(v) ? v.filter(Boolean).map(String) : [];
  } catch {
    return [];
  }
}

export function useInteractions({ clientId, contactIds } = {}) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Effect deps are primitives so a fresh { contactIds: [id] } object on every
  // render does not tear down and rebuild the listeners.
  const clientKey = clientId || "";
  const contactKey = serializeIds(contactIds);

  useEffect(() => {
    const ids = deserializeIds(contactKey);
    if (!clientKey && ids.length === 0) {
      setRecords([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    if (deniedAt && Date.now() - deniedAt < DENIED_RETRY_MS) {
      setRecords([]);
      setLoading(false);
      setError(new Error("permission-denied"));
      return undefined;
    }

    let cancelled = false;
    // The previous subject's records must not sit under the new subject's
    // heading while the new listeners are still settling.
    setRecords([]);
    setLoading(true);
    setError(null);

    let queries;
    try {
      queries = [];
      if (clientKey) queries.push(query(collection(db, COLLECTION), where("clientId", "==", clientKey)));
      for (const id of ids) {
        queries.push(query(collection(db, COLLECTION), where("contactIds", "array-contains", id)));
      }
    } catch (err) {
      setError(err);
      setLoading(false);
      return undefined;
    }

    const buckets = queries.map(() => []);
    const settled = queries.map(() => false);

    const publish = () => {
      if (cancelled) return;
      const byId = new Map();
      for (const bucket of buckets) {
        for (const rec of bucket) if (rec && rec.id) byId.set(rec.id, rec);
      }
      setRecords(sortRecords([...byId.values()]));
      if (settled.every(Boolean)) setLoading(false);
    };

    const unsubs = queries.map((q, i) => onSnapshot(
      q,
      (snap) => {
        // The document id wins over any `id` field inside the document — the
        // two agree on every live record, and where they ever disagree the
        // path is the one the SDK can read the document back by.
        buckets[i] = snap.docs.map(d => ({ ...d.data(), id: d.id }));
        settled[i] = true;
        publish();
      },
      (err) => {
        settled[i] = true;
        if (err?.code === "permission-denied") deniedAt = Date.now();
        if (cancelled) return;
        // console.warn, not error: a missing rule is an expected state today
        // and must not read as a crash. The Firestore SDK logs its own line.
        console.warn("[useInteractions]", err?.code || err?.message || err);
        setError(err);
        publish();
      },
    ));

    return () => {
      cancelled = true;
      for (const unsub of unsubs) {
        try { unsub(); } catch { /* listener already torn down */ }
      }
    };
  }, [clientKey, contactKey]);

  return { records, loading, error };
}

export default useInteractions;
